"use client";

import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { BiteButton } from "./bite-ui";

export function BiteCoverSkeleton() {
  return <div className="bite-cover-skeleton" role="status" aria-label="소장 캐릭터를 만들고 있습니다">
    <span className="bite-cover-skeleton__shimmer" aria-hidden="true" />
  </div>;
}

export interface BiteCardArtState {
  status: "missing" | "generating" | "ready" | "failed";
  url: string | null;
}

export function BiteSavedCardArt({art,companyName}:{art?:BiteCardArtState;companyName:string}) {
  const [loaded,setLoaded]=useState('');
  const [failed,setFailed]=useState('');
  const image=useRef<HTMLImageElement>(null);
  const source=art?.status==='ready'?art.url:null;
  useEffect(()=>{if(source&&image.current?.complete&&image.current.naturalWidth>0)setLoaded(source);},[source]);
  if(art?.status==='failed'||(source&&failed===source)) return <div className="bite-card-art-status" role="status"><p>캐릭터를 불러오지 못했습니다.</p></div>;
  if(!source) return <BiteCoverSkeleton/>;
  return <>
    {loaded!==source&&<BiteCoverSkeleton/>}
    <div className="bite-bulls-card__art" data-loaded={loaded===source}>
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img ref={image} src={source} alt={`${companyName} 소장 캐릭터`} loading="lazy" onLoad={()=>setLoaded(source)} onError={()=>setFailed(source)}/>
    </div>
  </>;
}

export function useBiteCardArt(jobId?: string, initial?: BiteCardArtState) {
  const [state, setState] = useState<BiteCardArtState>(initial || { status: "missing", url: null });
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    if (!jobId) return;
    if (initial?.status === "ready" && attempt === 0) { setState(initial); return; }
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;
    const endpoint = `/api/ticker/bite/cards/${encodeURIComponent(jobId)}/art`;
    const check = async (start = false) => {
      try {
        const next = await api<BiteCardArtState>(endpoint, start ? { method: "POST", body: { retry: attempt > 0 } } : {});
        if (cancelled) return;
        setState(next);
        if (next.status === "missing") { void check(true); return; }
        if (next.status === "generating") timer = setTimeout(() => { void check(); }, 3000);
      } catch { if (!cancelled) setState({ status: "failed", url: null }); }
    };
    setState({ status: "generating", url: null });
    void check(true);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [jobId, initial?.status, initial?.url, attempt]);
  return { state, attempt, retry: () => setAttempt(value => value + 1), imageFailed: () => setState({ status: "failed", url: null }) };
}

export function BiteCardArt({ jobId, initial, companyName }: { jobId?: string; initial?: BiteCardArtState; companyName: string }) {
  const { state, attempt, retry, imageFailed } = useBiteCardArt(jobId, initial);
  const [loadedImage, setLoadedImage] = useState("");
  const source = state.url ? `${state.url}${state.url.includes("?") ? "&" : "?"}attempt=${attempt}` : "";
  if (state.status === "ready" && source) return <>
    {loadedImage !== source && <BiteCoverSkeleton />}
    <div className="bite-bulls-card__art" data-loaded={loadedImage === source}>
    {/* eslint-disable-next-line @next/next/no-img-element */}
    <img src={source} alt={`${companyName} 소장 캐릭터`} onLoad={() => setLoadedImage(source)} onError={imageFailed} />
    </div>
  </>;
  if (state.status !== "failed") return <BiteCoverSkeleton />;
  return <div className="bite-card-art-status" role="status" data-art-status={state.status}>
    <p>캐릭터를 불러오지 못했습니다.</p><BiteButton variant="secondary" size="sm" onClick={retry}>다시 불러오기</BiteButton>
  </div>;
}
